import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import dayjs from "dayjs";
import { formatCompactNumber } from "../utils/numbers";

/**
 * Props:
 *  - xData: array of numbers, dates (ISO strings) or labels
 *  - projection: number[] (y values for Projection)
 *  - target: number[] (y values for Target)
 *  - height: number (px) default 220
 *  - theme: string|null (echarts theme name or null)
 */
export default function ProjectionTargetChart({
  xData = [0, 1, 2, 3, 4, 5],
  projection = [0, 500000, 2000000, 3500000, 6200000, 10000000],
  target = [0, 700000, 2300000, 3800000, 6800000, 11000000],
  height = 220,
  theme = null,
}) {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // helper: is xData categorical (strings or Dates) or numeric
  const isCategoryX = (arr) => arr.length > 0 && arr.every((v) => typeof v === "string" || Object.prototype.toString.call(v) === "[object Date]");

  // create / dispose chart when theme or ref changes; otherwise reuse instance
  useEffect(() => {
    if (!chartRef.current) return;
    const container = chartRef.current;

    // init chart once (or re-init on theme change)
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(container, theme);
    } else {
      // if theme changed: safe re-init
      try {
        chartInstanceRef.current.dispose();
      } catch (e) {}
      chartInstanceRef.current = echarts.init(container, theme);
    }

    // ResizeObserver to auto-resize when container size changes (handles modal open)
    const ro = new ResizeObserver((entries) => {
      for (const ent of entries) {
        if (ent.contentRect) {
          chartInstanceRef.current?.resize();
        }
      }
    });
    // observe container's parent too if needed (some modals set parent size after show)
    ro.observe(container);
    if (container.parentElement) ro.observe(container.parentElement);

    // also handle window resize
    const onWinResize = () => chartInstanceRef.current?.resize();
    window.addEventListener("resize", onWinResize);

    // ensure immediate resize after init (next tick)
    setTimeout(() => {
      chartInstanceRef.current?.resize();
    }, 0);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onWinResize);
      chartInstanceRef.current?.dispose();
      chartInstanceRef.current = null;
    };
  }, [theme]);

  useEffect(() => {
    if (!chartInstanceRef.current) return;

    // Defensive: ensure xData/projection/target lengths align; use null for missing.
    const maxLen = Math.max(xData?.length ?? 0, projection?.length ?? 0, target?.length ?? 0);

    const projData = new Array(maxLen).fill(null).map((_, i) => {
      const y = projection?.[i];
      return typeof y === "number" && !isNaN(y) ? y : null;
    });
    const tgtData = new Array(maxLen).fill(null).map((_, i) => {
      const y = target?.[i];
      return typeof y === "number" && !isNaN(y) ? y : null;
    });

    // Build x axis data (category) if needed
    const useCategory = isCategoryX(xData);
    const xAxisData = useCategory ? xData.map((d) => (d instanceof Date ? dayjs(d).format("YYYY-MM-DD") : String(d))) : undefined;

    // All numeric values to determine max
    const allY = [...projData, ...tgtData].filter((v) => typeof v === "number" && isFinite(v));
    const rawMax = allY.length ? Math.max(...allY) : 1;

    // Create a nicely rounded max and step for splitNumber segments
    const splitNumber = 4;
    const computeNiceMax = (value, splits) => {
      if (!isFinite(value) || value <= 0) return 1;
      // base magnitude
      const mag = Math.pow(10, Math.floor(Math.log10(value / splits)));
      // find multiple of mag that covers value/splits
      const stepMultiple = Math.ceil(value / splits / mag);
      const step = stepMultiple * mag;
      const niceMax = step * splits;
      return niceMax;
    };
    const niceMax = computeNiceMax(rawMax, splitNumber);

    // Axis label formatter (millions)
    const formatMillions = (val) => {
      if (val === 0) return "0";
      const m = val / 1_000_000;
      // keep readable: show 0.5M, 1.2M or 10M
      if (m >= 10) return `${Math.round(m)}M`;
      if (m >= 1) return `${m.toFixed(m >= 2 ? 0 : 1)}M`;
      return `${m.toFixed(2)}M`;
    };

    // Tooltip value formatting (full comma-separated)
    const formatWithCommas = (n) => {
      if (n == null || n === "" || !isFinite(n)) return "—";
      return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    // Final target marking: pick last non-null target value if exists
    const lastTarget = (() => {
      for (let i = tgtData.length - 1; i >= 0; i--) {
        if (tgtData[i] != null) return tgtData[i];
      }
      return null;
    })();

    const options = {
      backgroundColor: "transparent",
      grid: {
        left: 36,
        right: 20,
        top: 16,
        bottom: 36,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        backgroundColor: "rgba(24,28,40,0.95)",
        padding: [10, 12],
        textStyle: { color: "#fff" },
        formatter: (params) => {
          // params: array of series at this x index
          // for category x: use params[0].axisValue; for numeric x: use value[0] if value is [x,y]
          const axisValue = params?.[0]?.axisValue ?? "";
          const header = `<div style="font-weight:700;margin-bottom:6px">${axisValue}</div>`;
          const rows = [header];
          params.forEach((p) => {
            // p.value might be number (category x) or [x,y] (value axis)
            const y = Array.isArray(p.value) ? p.value[1] : p.value;
            rows.push(`<div style="color:${p.color};margin-top:4px">${p.seriesName}: <strong>${formatWithCommas(y)}</strong></div>`);
          });
          return rows.join("");
        },
      },
      legend: {
        data: ["Projection", "Target"],
        bottom: -4,
        left: "center",
        itemGap: 24,
        textStyle: { color: theme === "dark" ? "#a5a5a5ff" : "#222" },
      },

      xAxis: {
        type: useCategory ? "category" : "value",
        data: useCategory ? xAxisData : undefined,
        min: useCategory ? undefined : xData.length ? Math.min(...xData) : 0,
        max: useCategory ? undefined : xData.length ? Math.max(...xData) : 5,
        splitLine: { show: false },
        axisLine: { show: true, lineStyle: { color: "#dcdcdc" } },
        axisTick: { show: false },
        axisLabel: { color: "#666", fontSize: 12 },
      },

      yAxis: {
        type: "value",
        min: 0,
        max: niceMax,
        splitNumber,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: "#666", formatter: (val) => formatCompactNumber(val), fontSize: 12 },
        splitLine: {
          show: true,
          lineStyle: { color: "rgba(0,0,0,0.06)" },
        },
      },

      series: [
        {
          name: "Projection",
          type: "line",
          showSymbol: false,
          smooth: true,
          data: useCategory ? projData.map((v) => (v == null ? null : v)) : xData.map((x, i) => (projData[i] == null ? [x, null] : [x, projData[i]])),
          lineStyle: { width: 3, color: "#4A90E2" },
          areaStyle: {
            // subtle area under the projection line, optional
            color: "rgba(74,144,226,0.06)",
          },
        },
        {
          name: "Target",
          type: "line",
          showSymbol: false,
          smooth: true,
          data: useCategory ? tgtData.map((v) => (v == null ? null : v)) : xData.map((x, i) => (tgtData[i] == null ? [x, null] : [x, tgtData[i]])),
          lineStyle: { width: 3, color: "#2CA58D" },
          // markLine highlights the final target; label styled and offset to avoid clipping
          markLine:
            lastTarget != null
              ? {
                  silent: true,
                  symbol: ["none", "none"],
                  data: [{ yAxis: lastTarget }],
                  lineStyle: { color: "#2CA58D", type: "dashed" },
                  label: {
                    show: false,
                  },
                  //   label: {
                  //     // position 'end' places it near the right end of the line
                  //     position: "end",
                  //     formatter: (params) => `Target: ${formatWithCommas(params && params.value ? params.value : lastTarget)}`,
                  //     color: "#2CA58D",
                  //     backgroundColor: theme === 'dark' ? "rgba(45, 55, 65, 0.95)" : "rgba(255,255,255,0.95)",
                  //     padding: [6, 8],
                  //     borderRadius: 6,
                  //     // align/offset ensure label does not clip at right edge
                  //     align: "right",
                  //     verticalAlign: "middle",
                  //   },
                }
              : undefined,
        },
      ],
    };

    chartInstanceRef.current.setOption(options, { notMerge: true });

    // resize after setting
    setTimeout(() => chartInstanceRef.current?.resize(), 0);
  }, [xData, projection, target, theme]);

  return <div ref={chartRef} style={{ width: "100%", height: `${height}px` }} />;
}
